
import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CreditProvider } from './contexts/CreditContext';
 
// Layouts
import MainLayout, { PublicLayout } from './layouts/MainLayout';
import PartnerLayout from './layouts/PartnerLayout';
import AdminLayout from './layouts/AdminLayout';
import LoadingSpinner from './components/LoadingSpinner';
 
// Core Pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const CompleteProfilePage = lazy(() => import('./pages/CompleteProfilePage'));
 
// Founder Pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const DnaAssessmentPage = lazy(() => import('./pages/DnaAssessmentPage'));
const DeepDnaAssessmentPage = lazy(() => import('./pages/DeepDnaAssessmentPage'));
const AssessmentChoicePage = lazy(() => import('./pages/AssessmentChoicePage'));
const ContentGenerationPage = lazy(() => import('./pages/ContentGenerationPage'));
const HubDashboardPage = lazy(() => import('./pages/HubDashboardPage'));
const ContentLibraryPage = lazy(() => import('./pages/ContentLibraryPage'));
const DNAProfilePage = lazy(() => import('./pages/DNAProfilePage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const BrandKitPage = lazy(() => import('./pages/BrandKitPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const TeamManagementPage = lazy(() => import('./pages/TeamManagementPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
 
// Partner Pages
const PartnerDashboardPage = lazy(() => import('./pages/PartnerDashboardPage'));
const ClientDetailPage = lazy(() => import('./pages/ClientDetailPage'));
const PartnerBillingPage = lazy(() => import('./pages/PartnerBillingPage'));
 
// Admin Pages
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
 
// Public Pages
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const PartnerApplyPage = lazy(() => import('./pages/PartnerApplyPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const PartnerTermsPage = lazy(() => import('./pages/PartnerTermsPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Admin gating reads from the Firebase Auth custom claim
// (request.auth.token.admin === true) surfaced by AuthContext as
// `claims.admin`. Set via the setAdminClaim Cloud Function.

const App = () => (
    <AuthProvider>
        <CreditProvider>
            <Router>
                <Suspense fallback={<LoadingSpinner fullPage />}>
                <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<PublicLayout><LandingPage /></PublicLayout>} />
                    <Route path="/login" element={<PublicLayout><LoginPage /></PublicLayout>} />
                    <Route path="/signup" element={<PublicLayout><SignupPage /></PublicLayout>} />
                    <Route path="/pricing" element={<PublicLayout><PricingPage /></PublicLayout>} />
                    <Route path="/complete-profile" element={<CompleteProfilePage />} />
                    <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
                    <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
                    <Route path="/partners" element={<PublicLayout><PartnersPage /></PublicLayout>} />
                    <Route path="/partner-apply" element={<PublicLayout><PartnerApplyPage /></PublicLayout>} />
                    <Route path="/privacy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
                    <Route path="/terms" element={<PublicLayout><TermsOfServicePage /></PublicLayout>} />
                    <Route path="/partner-terms" element={<PublicLayout><PartnerTermsPage /></PublicLayout>} />
 
                    {/* Protected Founder Routes */}
                    <Route path="/dashboard" element={<ProtectedRoute><MainLayout><DashboardPage /></MainLayout></ProtectedRoute>} />
                    <Route path="/dashboard/billing" element={<ProtectedRoute><MainLayout><BillingPage /></MainLayout></ProtectedRoute>} />
                    <Route path="/dashboard/:hubId" element={<ProtectedRoute><MainLayout><HubDashboardPage /></MainLayout></ProtectedRoute>} />
                    <Route path="/onboarding" element={<ProtectedRoute><MainLayout><OnboardingPage /></MainLayout></ProtectedRoute>} />
                    <Route path="/assessment" element={<ProtectedRoute><MainLayout><AssessmentChoicePage /></MainLayout></ProtectedRoute>} />
                    <Route path="/assessment/quick" element={<ProtectedRoute><MainLayout><DnaAssessmentPage /></MainLayout></ProtectedRoute>} />
                    <Route path="/assessment/deep" element={<ProtectedRoute><MainLayout><DeepDnaAssessmentPage /></MainLayout></ProtectedRoute>} />
                    {/* legacy alias so old links still work */}
                    <Route path="/dna-assessment" element={<Navigate to="/assessment" replace />} />
                    <Route path="/dna-profile" element={<ProtectedRoute><MainLayout><DNAProfilePage /></MainLayout></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><MainLayout><ProfilePage /></MainLayout></ProtectedRoute>} />
                    <Route path="/content-generation/:hub" element={<ProtectedRoute><MainLayout><ContentGenerationPage /></MainLayout></ProtectedRoute>} />
                    <Route path="/content-generation" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/content-library" element={<ProtectedRoute><MainLayout><ContentLibraryPage /></MainLayout></ProtectedRoute>} />
                    <Route path="/analytics" element={<ProtectedRoute><MainLayout><AnalyticsPage /></MainLayout></ProtectedRoute>} />
                    <Route path="/brand-kit" element={<ProtectedRoute><MainLayout><BrandKitPage /></MainLayout></ProtectedRoute>} />
                    <Route path="/settings" element={<ProtectedRoute><MainLayout><SettingsPage /></MainLayout></ProtectedRoute>} />
                    <Route path="/settings/team" element={<ProtectedRoute><MainLayout><TeamManagementPage /></MainLayout></ProtectedRoute>} />
 
                    {/* Protected Partner Routes */}
                    <Route path="/partner-dashboard" element={<ProtectedRoute requiredRole="agency"><PartnerLayout><PartnerDashboardPage /></PartnerLayout></ProtectedRoute>} />
                    <Route path="/partner-dashboard/client/:clientId" element={<ProtectedRoute requiredRole="agency"><PartnerLayout><ClientDetailPage /></PartnerLayout></ProtectedRoute>} />
                    <Route path="/partner-dashboard/billing" element={<ProtectedRoute requiredRole="agency"><PartnerLayout><PartnerBillingPage /></PartnerLayout></ProtectedRoute>} />
 
                    {/* Protected Admin Routes (claim-gated) */}
                    <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                        <Route index element={<AdminDashboardPage />} />
                    </Route>
 
                    {/* 404 catch-all — must be LAST */}
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
                </Suspense>
            </Router>
        </CreditProvider>
    </AuthProvider>
);
 
const ProtectedRoute = ({ children, requiredRole }) => {
    const { currentUser, userData, loading } = useAuth();
 
    if (loading) {
        return <LoadingSpinner fullPage />;
    }
 
    if (!currentUser) {
        return <Navigate to="/login" replace />;
    }
 
    if (requiredRole && userData?.role !== requiredRole) {
        return <Navigate to={userData?.role === 'agency' ? '/partner-dashboard' : '/dashboard'} replace />;
    }
 
    if (!requiredRole && userData?.role === 'agency') {
         return <Navigate to="/partner-dashboard" replace />;
    }
 
    if (requiredRole === 'agency' && userData?.role === 'founder') {
         return <Navigate to="/dashboard" replace />;
    }
 
    return children;
};
 
const AdminRoute = ({ children }) => {
    const { currentUser, claims, loading } = useAuth();

    if (loading) {
        return <LoadingSpinner fullPage />;
    }

    if (!currentUser) {
        return <Navigate to="/login" replace />;
    }

    // Authoritative admin check: Firebase Auth custom claim.
    // If a user was just promoted via setAdminClaim, they need to
    // sign out + in (or call user.getIdToken(true)) for the new
    // claim to appear here.
    if (claims?.admin !== true) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
};
 
export default App;
 